import { store } from "../redux/_helpers";
import { actions } from "../redux/_actions";
import utils from "../utils";

const API_URL = utils.getEnv('REACT_APP_API_URL', "http://localhost:3010/api");

const call = async (url, _data, options = {}) => {
  const token = localStorage.getItem("token") || "";
  const ce_id = localStorage.getItem("ce_id") || "";
  const data = { token, ce_id, ..._data };
  const skipLoading = options.skipLoading === true;

  if (!skipLoading) store.dispatch(actions.setLoading(true));

  try {
    const response = await fetch(API_URL + url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!skipLoading) store.dispatch(actions.setLoading(false));

    if (!response.ok) {
      const text = await response.text();
      let errorMessage = response.statusText;
      let json = null;
      try {
        json = JSON.parse(text);
        errorMessage = json.error || errorMessage;
      } catch (_) {
        if (text.startsWith("<")) errorMessage = `Server error ${response.status}`;
      }
      const err = new Error(errorMessage);
      err.status = response.status;
      if (json) {
        err.details = json.details;
        if (json.intent !== undefined) err.intent = json.intent;
      }
      throw err;
    }

    return await response.json();
  } catch (error) {
    if (!skipLoading) store.dispatch(actions.setLoading(false));
    // Don't log 4xx (validation, auth, not found) as errors; only log 5xx or unexpected failures
    const status = error.status;
    const msg = typeof error.message === "string" ? error.message : "";
    const isOffline =
      !status &&
      (error.name === "TypeError" ||
        /failed to fetch|networkerror|load failed|err_connection_refused/i.test(msg));
    if (!(status >= 400 && status < 500) && (!status || status >= 500) && !isOffline) {
      console.error("Error occurred during API call:", error);
    }
    if (isOffline) {
      error.message =
        "Could not reach the OllaCart API. For local development, run the server on port 3010 and open the app from http://localhost:3009 (or the host your dev server lists).";
    }
    throw error;
  }
};

const me = () => {
  return call(`/auth/me`, {});
};

const signin = (email, referralCode) => {
  const payload = { email };
  if (referralCode) payload.referralCode = referralCode;
  return call(`/signin`, payload);
};

const deleteAccount = () => {
  return call(`/user/delete`, {});
};

const request = (email) => {
  return call(`/request`, { email });
};

const verifyUser = () => {
  return call(`/verify`, {}, { skipLoading: true });
};

const getProduct = (productId) => {
  return call(`/product/detail/${productId}`, {});
};

const getProducts = (filter, options) => {
  return call(`/products/by/search`, filter, options);
};

/** Count of products in purchase cart (for header badge). Skips global loading to avoid spinner loop. */
const getPurchaseCartCount = () => {
  return call(`/products/cart-count`, {}, { skipLoading: true });
};

const shareProduct = (productID, shared) => {
  return call(`/share/${productID}`, { shared });
};

const putCart = (productID, shared, purchased) => {
  return call(`/putcart/${productID}`, { shared, purchased });
};

const updateProduct = (productID, detail) => {
  return call(`/product/update/${productID}`, detail);
};

const updateProductLogo = (productID, idx) => {
  return call(`/product/updatelogo/${productID}`, { idx });
};

const updateProductSequence = (data) => {
  return call(`/product/update_sequence`, { data });
};

const removeProduct = (productID) => {
  return call(`/product/remove/${productID}`);
};

const getUsers = (pagination, filters = {}) => {
  return call(`/admin/getusers/`, { pagination, ...filters });
};

const adminUpdateUser = (payload) => {
  return call(`/admin/updateuser`, payload);
};

const getCategories = (pagination) => {
  // Use public endpoint for regular users, admin endpoint for admin users
  // For now, use public endpoint - it's read-only and safe
  return call(`/category/list`, {});
};

/** Lists + categories present in a cart (same filter as getProducts), with counts. */
const getFacets = (filter) => {
  return call(`/products/facets`, filter || {}, { skipLoading: true });
};

/** A shopper's own category, made from a cart's chip row. */
const createMyCategory = (name) => {
  return call(`/category/mine/create`, { name });
};

const getTaxes = (detail = {}) => {
  return call(`/admin/gettaxes/`, detail);
};

const getCarts = (pagination, filter = {}, options = {}) => {
  return call(`/admin/getcarts/`, {
    pagination,
    filter,
    search: options.search,
    sortBy: options.sortBy,
    sortDir: options.sortDir,
  });
};

const getOrders = (pagination, filters = {}) => {
  return call(`/admin/getorders/`, { pagination, ...filters });
};

const getAnalytics = () => {
  return call(`/admin/getanalytics/`, {});
};

const getPlatformStats = () => {
  return call(`/platform/stats`, {}, { skipLoading: true });
};

/** Product images from user carts for landing hero + shopping feed */
const getPlatformShowcase = (limit = 18, options = {}) => {
  const { skip = 0, random = false, sharedOnly = false } = options;
  return call(
    `/platform/showcase`,
    { limit, skip, random, sharedOnly },
    { skipLoading: true }
  );
};

const getFeedbacks = (pagination) => {
  return call(`/admin/getfeedbacks/`, { pagination });
};

const getPartnerRequests = (pagination) => {
  return call(`/admin/getpartnerrequests/`, { pagination });
};

const getInvestorRequests = (pagination) => {
  return call(`/admin/getinvestorrequests/`, { pagination });
};

const getRetailerApplications = ({ page = 1, limit = 20, status } = {}) => {
  return call(`/admin/retailer-applications`, { page, limit, status });
};

const getAdminRetailers = ({
  page = 1,
  limit = 20,
  search,
  claimed,
  setupMode,
  sortBy,
  sortDir,
} = {}) => {
  return call(`/admin/retailers`, {
    page,
    limit,
    search,
    claimed,
    setupMode,
    sortBy,
    sortDir,
  });
};

const approveRetailerApplication = ({ applicationId, commissionRate, notes, setupMode }) => {
  return call(`/admin/retailer-approve`, { applicationId, commissionRate, notes, setupMode });
};

const rejectRetailerApplication = ({ applicationId, notes }) => {
  return call(`/admin/retailer-reject`, { applicationId, notes });
};

const backfillRetailers = () => {
  return call(`/admin/backfill-retailers`, {});
};

const verifySecure = (uid) => {
  return call(`/auth/verifysecure/`, { uid });
};

const verifySignin = (uid) => {
  return call(`/auth/verifysignin/`, { uid });
};

const checkSecureVerified = (uid) => {
  return call(`/auth/checkSecureVerified/`, { uid });
};

const fetchPurchaseLink = () => {
  return call(`/stripe/fetchpurchaselink`, {});
};

const createAnonymousUsernamePaymentIntent = () => {
  return call(`/stripe/createanonymoususernamepaymentintent`, {});
};

/** Rye Checkout Intents: create intent + poll until offer ready. Body: { productUrl, quantity?, buyer } */
const createRyeCheckoutIntent = (payload) => {
  return call(`/rye/checkout/create`, payload);
};

/** Rye Checkout: single-step purchase. Body: { productUrl, quantity?, buyer, paymentMethod?, constraints? } */
const ryePurchase = (payload) => {
  return call(`/rye/checkout/purchase`, payload);
};

/** Rye Checkout: get intent state. Body or query: { intentId } */
const getRyeCheckoutIntent = (intentId) => {
  return call(`/rye/checkout/intent`, { intentId });
};

/** Rye Checkout: confirm intent with payment (multi-step). Body: { intentId, paymentMethod: { type, stripeToken } } */
const confirmRyeCheckoutIntent = (payload) => {
  return call(`/rye/checkout/confirm`, payload);
};

/** Guest gift: create intent (no sign-in). Body: { productUrl, quantity?, recipient, shareOwnerId, payerEmail?, website? } */
const createRyeGuestGiftCheckoutIntent = (payload) => {
  return call(`/rye/checkout/guest/gift/create`, payload);
};

/** Guest gift: confirm. Body: { intentId, paymentMethod, guestToken } */
const confirmRyeGuestGiftCheckoutIntent = (payload) => {
  return call(`/rye/checkout/guest/gift/confirm`, payload);
};

// Zinc Universal Checkout (replaces Rye). Customer pays via Stripe first —
// createZincPaymentIntent returns a clientSecret to confirm with Stripe
// Elements (see components/ZincCheckout/ZincCheckoutModal.js); the Zinc
// order itself is placed server-side once that payment succeeds, so poll
// zincGetOrderStatusByPaymentIntent to see it.
/** Body: { productUrl, quantity?, price, checkoutMode?, recipient?, shareOwnerId?, recipientUserId?, address?, payerEmail?, isGift?, giftMessage? } */
/** Saved shipping / card on file + whether this is the buyer's first order. */
const getSavedCheckoutDetails = () => {
  return call(`/zinc/checkout/saved-details`, {}, { skipLoading: true });
};

const createZincPaymentIntent = (payload) => {
  return call(`/zinc/checkout/payment-intent`, payload);
};

/** Poll by paymentIntentId (before any Zinc orderId exists yet). */
const getZincOrderStatusByPaymentIntent = (paymentIntentId) => {
  return call(`/zinc/checkout/status-by-payment-intent`, { paymentIntentId }, { skipLoading: true });
};

/** Poll by the Zinc order id once known. */
const getZincOrderStatus = (orderId) => {
  return call(`/zinc/checkout/status`, { orderId }, { skipLoading: true });
};

const cancelZincOrder = (orderId) => {
  return call(`/zinc/checkout/cancel`, { orderId });
};

const fetchProductsByClientSecret = (clientSecret) => {
  return call(`/order/productsbyclientsecret`, { clientSecret });
};

const updateOrderDetail = (orderId, detail) => {
  return call(`/order/updatedetail/${orderId}`, detail);
};

const getOrderedProducts = () => {
  return call(`/order/getorderedproducts`, {});
};

const getOrdersByUser = () => {
  return call(`/order/getordersbyuser`, {});
};

const getShareStatus = (_id) => {
  return call(`/product/getsharestatus`, { _id });
};

const getSocialStatus = (_id) => {
  return call(`/product/getsocialstatus`, {});
};

const inviteUser = (email) => {
  return call(`/user/invite`, { email });
};

const followUser = (followId, email = "") => {
  return call(`/user/follow`, { followId, email });
};

const unFollowUser = (followId) => {
  return call(`/user/unfollow`, { followId });
};

const forkProduct = (productID, email) => {
  return call(`/product/fork/${productID}`, { email });
};

const adminAddProduct = (userID, productID) => {
  return call(`/product/adminadd/${productID}`, { userID });
};

const thumbup = (productID, email) => {
  return call(`/product/thumbup/${productID}`, { email });
};

const thumbdown = (productID, email) => {
  return call(`/product/thumbdown/${productID}`, { email });
};

/** AI-suggested name / description / category for a product (not saved). */
const optimizeListing = (productID) => {
  return call(`/product/optimize-listing/${productID}`, {}, { skipLoading: true });
};

/** Re-read the store page and overwrite name/price/description/photos.
 * Resolves { product } when done, or { pending: true } if it's still running. */
const refreshFromWebsite = (productID) => {
  return call(`/product/refresh-from-website/${productID}`, {}, { skipLoading: true });
};

/** Who liked a product (owner, or anyone once it's shared). */
// Favorites (people you share with) + direct shares and the inbox.
const getFavorites = () => call(`/social/favorites`, {}, { skipLoading: true });
const addFavorite = (who) => call(`/social/favorites/add`, who, { skipLoading: true });
const removeFavorite = (userId) => call(`/social/favorites/remove`, { userId }, { skipLoading: true });
/** { productId, toUserIds, emails, note, saveToFavorites } */
const sendShare = (payload) => call(`/social/send`, payload);
const getShareInbox = () => call(`/social/inbox`, {}, { skipLoading: true });
const markShareInboxRead = () => call(`/social/inbox/read`, {}, { skipLoading: true });

const getLikes = (productID) => {
  return call(`/product/likes/${productID}`, {}, { skipLoading: true });
};

const singleShare = (productID, email) => {
  return call(`/product/singleshare/${productID}`, { email });
};

const anonymousShare = (productID, email) => {
  return call(`/product/anonymousshare/${productID}`, { email });
};

const sendInvestContact = (detail) => {
  return call(`/contact/invest`, detail);
};

const sendPartnerContact = (detail) => {
  return call(`/contact/partner`, detail);
};

const sendFeedback = (detail) => {
  return call(`/contact/feedback`, detail);
};

const getAccountSettings = () => {
  return call(`/user/getaccountsettings`, {}, { skipLoading: true });
};

const updateAccountSettings = (detail) => {
  return call(`/user/updateaccountsettings`, detail);
};

const completeTutorial = () => {
  return call(`/user/complete-tutorial`, {}, { skipLoading: true });
};

const createCategory = (detail) => {
  return call(`/category/create`, detail);
};

const updateCategory = (id, detail) => {
  return call(`/category/update/${id}`, detail);
};

const removeCateogory = (id) => {
  return call(`/category/remove/${id}`, {});
};

const getMyCollections = () => {
  return call(`/collection/list`, {});
};

const getCollectionsByUser = (userId) => {
  return call(`/collection/list-by-user`, { userId });
};

const createCollection = (detail) => {
  return call(`/collection/create`, detail);
};

const updateCollection = (id, detail) => {
  return call(`/collection/update/${id}`, detail);
};

const removeCollection = (id) => {
  return call(`/collection/remove/${id}`, {});
};

// Shared lists: invite people to add to one list; a view-only link to it.
const getCollectionMembers = (id) => call(`/collection/members/${id}`, {}, { skipLoading: true });
/** { email? } — returns { inviteLink }; emails it when an email is given. */
const inviteToCollection = (id, detail = {}) => call(`/collection/invite/${id}`, detail, { skipLoading: true });
const resetCollectionInvite = (id) => call(`/collection/reset-invite/${id}`, {}, { skipLoading: true });
const getCollectionViewLink = (id, reset = false) => call(`/collection/view-link/${id}`, { reset }, { skipLoading: true });
const disableCollectionViewLink = (id) => call(`/collection/disable-view-link/${id}`, {}, { skipLoading: true });
/** Owner removes someone, or omit userId to leave the list yourself. */
const removeCollectionMember = (id, userId) => call(`/collection/remove-member/${id}`, userId ? { userId } : {});
const joinCollection = (code) => call(`/collection/join`, { code });
const viewCollectionByCode = (code) => call(`/collection/view`, { code });

const getComments = (productID) => call(`/product/comments/${productID}`, {}, { skipLoading: true });
const getNotifications = (limit) => call(`/notifications`, { limit }, { skipLoading: true });
/** ids omitted → mark everything read. */
const markNotificationsRead = (ids) => call(`/notifications/read`, ids ? { ids } : {}, { skipLoading: true });
const addComment = (productID, text) => call(`/product/comment/${productID}`, { text }, { skipLoading: true });
const deleteComment = (productID, commentId) => call(`/product/comment-delete/${productID}`, { commentId }, { skipLoading: true });

const updateTax = (detail) => {
  return call(`/tax/update`, detail);
};

const scanPage = (detail) => {
  return call(`/product/scanpage`, detail);
};

/** Save from a pasted product link → { scanId, pending } or { product }. */
const addByUrl = (url) => call(`/product/add-by-url`, { url }, { skipLoading: true });
/** → { status: "pending" | "success" | "failed", product? } */
const getScanStatus = (scanId) => call(`/product/scan-status/${scanId}`, {}, { skipLoading: true });

const getScanningUrls = () => {
  return call(`/product/getScanningUrls`, {}, { skipLoading: true });
};

// Affiliate functions
const generateAffiliateLink = (productId) => {
  return call(`/affiliate/generate-link`, { productId });
};

const trackClick = (productId, options = {}) => {
  return call(`/affiliate/track-click`, { productId, ...options });
};

/**
 * Tracked store link for one product (GET — safe to window.open() directly
 * inside a click handler). shareOwnerId credits a share-cart owner.
 */
const affiliateGoUrl = (productId, { userId, shareOwnerId } = {}) => {
  const params = new URLSearchParams();
  if (userId) params.set("u", userId);
  if (shareOwnerId) params.set("s", shareOwnerId);
  const qs = params.toString();
  return `${API_URL}/affiliate/go/${productId}${qs ? `?${qs}` : ""}`;
};

// Purchase cart grouped into per-store checkout stages (services/checkout).
const getCheckoutPlan = (productIds) => {
  return call(`/checkout/plan`, { productIds }, { skipLoading: true });
};

// Plan links are API paths (/checkout/go?..., /affiliate/go/...).
const apiLink = (path) => `${API_URL}${path}`;

// Crossmint agent checkout page (agent-checkout/, served next to the API).
// Same origin in production, so it reads the session from localStorage; in
// dev it's on the API's port, so pass the token in the fragment (not sent
// to any server).
const agentCheckoutUrl = (productId) => {
  const apiOrigin = new URL(API_URL, window.location.href).origin;
  const url = `${apiOrigin}/agent-checkout/?product=${encodeURIComponent(productId)}`;
  if (apiOrigin === window.location.origin) return url;
  return `${url}#token=${encodeURIComponent(localStorage.getItem("token") || "")}`;
};

// Looks: shoppable photos with products pinned on them.
// query: { userIds } (Social feed) | { userId } | { productId }, plus { before, limit }
const listLooks = (query) => call(`/looks`, query, { skipLoading: true });
const createLook = (look) => call(`/looks/create`, look);
const toggleLookLike = (lookId) => call(`/looks/${lookId}/like`, {}, { skipLoading: true });
const deleteLook = (lookId) => call(`/looks/${lookId}/delete`, {});

/** items: [{ id, url }] -> { routing: { [id]: { inApp, support } } } */
const getCheckoutRouting = (items) => {
  return call(`/zinc/checkout/routing`, { items }, { skipLoading: true });
};

const getEarnings = () => {
  return call(`/affiliate/earnings`, {});
};

const getClickAnalytics = (filters = {}) => {
  return call(`/affiliate/clicks`, filters);
};

const requestPayout = () => {
  return call(`/affiliate/request-payout`, {});
};

const getPayoutHistory = () => {
  return call(`/affiliate/payouts`, {});
};

// Admin: AffiliateNetwork management
// Admin: Stores to monetize (top saved stores + affiliate program status)
const adminTopStores = (days) => call(`/admin/affiliate/top-stores`, { days });
const adminSaveMerchantProgram = (program) => call(`/admin/affiliate/merchant-program`, program);
const adminDiscoveredPrograms = () => call(`/admin/affiliate/discovered-programs`, {});

const getAdminAffiliateNetworks = () => {
  return call(`/admin/affiliate-networks`, {});
};

const createAffiliateNetwork = (network) => {
  return call(`/admin/affiliate-networks/create`, network);
};

const updateAffiliateNetwork = (networkId, network) => {
  return call(`/admin/affiliate-networks/update/${networkId}`, network);
};

const removeAffiliateNetwork = (networkId) => {
  return call(`/admin/affiliate-networks/remove/${networkId}`, {});
};

const pollAffiliateNetworksNow = () => {
  return call(`/admin/affiliate-networks/poll-now`, {});
};

// Wishlist functions
const toggleWishlist = (productId) => {
  return call(`/product/toggle-wishlist/${productId}`, {});
};

// AI description regeneration
const regenerateDescription = (productId, instructions = '') => {
  return call(`/product/regenerate-description/${productId}`, { instructions });
};

// Waitlist functions
const joinWaitlist = (email, name, source) => {
  return call(`/waitlist/join`, { email, name, source });
};

const checkWaitlistStatus = (email) => {
  return call(`/waitlist/status?email=${encodeURIComponent(email)}`, {});
};

// User tier and earnings
const getTierAndEarnings = () => {
  return call(`/user/tier-earnings`, {});
};

const upgradeTier = (tier) => {
  return call(`/user/upgrade-tier`, { tier });
};

const generateShareLink = (productId) => {
  return call(`/affiliate/links/generate`, { product_id: productId });
};

const getMyShareLinks = () => {
  return call(`/affiliate/links/mine`, {});
};

const getAffiliateWallet = () => {
  return call(`/wallet`, {});
};

// Retailer application functions
const applyRetailer = (formData) => {
  return call(`/retailer/apply`, formData);
};

const becomeHybrid = () => call(`/retailer/become-hybrid`, {});
const requestRetailerClaim = (email, domain) =>
  call(`/retailer/claim/request`, { email, domain });
const completeRetailerClaim = (claimToken, email) =>
  call(`/retailer/claim/complete`, { claimToken, email });
const getRetailerMe = () => call(`/retailer/me`, {});
const updateRetailerSettings = (settings) =>
  call(`/retailer/settings`, settings);
const getRetailerAnalytics = () => call(`/retailer/analytics`, {});
const getRetailerOrders = (pagination) =>
  call(`/retailer/orders`, { pagination });
const updateRetailerOrder = (payload) =>
  call(`/retailer/orders/update`, payload);
const getRetailerAffiliate = () => call(`/retailer/affiliate`, {});
const createPosOrder = (productId, shipping) =>
  call(`/retailer/pos/order`, { productId, shipping });

/**
 * Read a File object as a base64 data URL (browser only).
 * Resolves to a data: string; the backend processImage accepts data URIs and http(s) URLs.
 */
const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

/**
 * Upload a profile or background image file.
 * Reads the file locally and sends the base64 string to the settings endpoint.
 * field: 'profilePicture' | 'backgroundPicture'
 */
const uploadShareImage = async (file, field) => {
  const dataUrl = await readFileAsDataURL(file);
  return call(`/user/updateaccountsettings`, { [field]: dataUrl });
};

// Shopping Assistant
const assistantGetContext = () => call(`/assistant/context/get`, {});
const assistantUpdateContext = (context) => call(`/assistant/context/update`, { context });
const assistantSearch = (query) => call(`/assistant/search`, { query });
const assistantSearchByImage = (imageData) => call(`/assistant/search-image`, { imageData });
const getAIUsage = () => call(`/assistant/admin/usage`, {});
const assistantGetRequest = (requestId) => call(`/assistant/request/${requestId}`, {}, { skipLoading: true });
const assistantListRequests = () => call(`/assistant/list`, {});
const assistantSaveProduct = (requestId, resultIndex) => call(`/assistant/save/${requestId}/${resultIndex}`, {});
const assistantClarify = (query, conversation) => call(`/assistant/clarify`, { query, conversation });

// Conversational agent (persisted multi-turn session)
const assistantStartAgentSession = () => call(`/assistant/agent/sessions`, {});
const assistantSendAgentMessage = (sessionId, message) =>
  call(`/assistant/agent/sessions/${sessionId}/messages`, { message });

// Product wiki — "Watch price" button on a search result card.
const assistantWatchProductPrice = (result) =>
  call(`/assistant/wiki/watch`, {
    url: result.url,
    name: result.name,
    domain: result.domain,
    photo: result.photo,
    price: result.price,
  });
const assistantUnwatchProductPrice = (url) => call(`/assistant/wiki/unwatch`, { url });

export default {
  me,
  signin,
  deleteAccount,
  request,
  verifyUser,
  getProduct,
  getProducts,
  getPurchaseCartCount,
  shareProduct,
  putCart,
  updateProduct,
  updateProductLogo,
  removeProduct,
  getUsers,
  adminUpdateUser,
  getCategories,
  getFacets,
  createMyCategory,
  getTaxes,
  getCarts,
  getOrders,
  getAnalytics,
  getPlatformStats,
  getPlatformShowcase,
  getFeedbacks,
  getInvestorRequests,
  getPartnerRequests,
  getRetailerApplications,
  getAdminRetailers,
  approveRetailerApplication,
  rejectRetailerApplication,
  backfillRetailers,
  updateProductSequence,
  verifySecure,
  verifySignin,
  checkSecureVerified,
  fetchPurchaseLink,
  createAnonymousUsernamePaymentIntent,
  createRyeCheckoutIntent,
  confirmRyeCheckoutIntent,
  createRyeGuestGiftCheckoutIntent,
  confirmRyeGuestGiftCheckoutIntent,
  ryePurchase,
  getRyeCheckoutIntent,
  createZincPaymentIntent,
  getSavedCheckoutDetails,
  getZincOrderStatusByPaymentIntent,
  getZincOrderStatus,
  cancelZincOrder,
  fetchProductsByClientSecret,
  updateOrderDetail,
  getOrderedProducts,
  getOrdersByUser,
  getShareStatus,
  getSocialStatus,
  followUser,
  unFollowUser,
  forkProduct,
  adminAddProduct,
  thumbup,
  thumbdown,
  getLikes,
  optimizeListing,
  refreshFromWebsite,
  singleShare,
  anonymousShare,
  sendFeedback,
  sendInvestContact,
  sendPartnerContact,
  getAccountSettings,
  updateAccountSettings,
  completeTutorial,
  removeCateogory,
  updateCategory,
  createCategory,
  updateTax,
  getMyCollections,
  getCollectionsByUser,
  createCollection,
  updateCollection,
  removeCollection,
  getCollectionMembers,
  inviteToCollection,
  resetCollectionInvite,
  getCollectionViewLink,
  disableCollectionViewLink,
  removeCollectionMember,
  joinCollection,
  viewCollectionByCode,
  getComments,
  getNotifications,
  addByUrl,
  getScanStatus,
  markNotificationsRead,
  addComment,
  deleteComment,
  inviteUser,
  scanPage,
  getScanningUrls,
  generateAffiliateLink,
  trackClick,
  getFavorites,
  addFavorite,
  removeFavorite,
  sendShare,
  getShareInbox,
  markShareInboxRead,
  affiliateGoUrl,
  agentCheckoutUrl,
  getCheckoutPlan,
  apiLink,
  getCheckoutRouting,
  getEarnings,
  listLooks,
  createLook,
  toggleLookLike,
  deleteLook,
  getClickAnalytics,
  requestPayout,
  getPayoutHistory,
  getAdminAffiliateNetworks,
  adminTopStores,
  adminSaveMerchantProgram,
  adminDiscoveredPrograms,
  createAffiliateNetwork,
  updateAffiliateNetwork,
  removeAffiliateNetwork,
  pollAffiliateNetworksNow,
  toggleWishlist,
  regenerateDescription,
  joinWaitlist,
  checkWaitlistStatus,
  getTierAndEarnings,
  upgradeTier,
  generateShareLink,
  getMyShareLinks,
  getAffiliateWallet,
  applyRetailer,
  becomeHybrid,
  requestRetailerClaim,
  completeRetailerClaim,
  getRetailerMe,
  updateRetailerSettings,
  getRetailerAnalytics,
  getRetailerOrders,
  updateRetailerOrder,
  getRetailerAffiliate,
  createPosOrder,
  assistantGetContext,
  assistantUpdateContext,
  assistantSearch,
  assistantSearchByImage,
  getAIUsage,
  assistantGetRequest,
  assistantListRequests,
  assistantSaveProduct,
  assistantClarify,
  assistantStartAgentSession,
  assistantSendAgentMessage,
  assistantWatchProductPrice,
  assistantUnwatchProductPrice,
  uploadShareImage,
};
